// services/JotURLService.js
import CryptoJS from 'crypto-js';

class JotURLService {
  constructor(email, publicKey, privateKey) {
    this.email = email;
    this.publicKey = publicKey;
    this.privateKey = privateKey;
    this.sessionId = null;
    this.sessionDatetime = null;
    this.token = "tok_RWU2gstxvxDGmjfXnQmFBO40P6LaHMLy17";
    this.projectId = "637a497569487941596a6f436e6c433659542b312f773d3d";
    this.domainId = "69614c754d43363833652f69494e446c69554b4c51413d3d";
  }

  /**
   * Generate GMT datetime in required format
   */
  _getGmtDatetime() {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    const hours = String(now.getUTCHours()).padStart(2, '0');
    const minutes = String(now.getUTCMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}Z`;
  }

  /**
   * Generate HMAC_SHA256 password for login
   */
  _generatePassword(gmtDatetime) {
    const message = `${this.publicKey}:${gmtDatetime}`;
    const hash = CryptoJS.HmacSHA256(message, this.privateKey);
    return hash.toString(CryptoJS.enc.Hex);
  }

  /**
   * Generate authentication hash for API calls
   */
  _generateAuthHash() {
    const gmtDatetime = this._getGmtDatetime();
    const message = `${this.sessionId}:${gmtDatetime}`;
    const hash = CryptoJS.HmacSHA256(message, this.privateKey);
    const authHash = hash.toString(CryptoJS.enc.Hex);
    return { authHash, gmtDatetime };
  }

  /**
   * Authenticate and get session ID
   */
  async login() {
    try {
      const gmtDatetime = this._getGmtDatetime();
      const password = this._generatePassword(gmtDatetime);

      const url = new URL("https://joturl.com/a/i1/users/login");
      url.searchParams.append('username', this.email);
      url.searchParams.append('password', "Murtaza8797#");
      url.searchParams.append('_dt', gmtDatetime);

      console.log('🔐 Attempting JotURL login...');

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();

        if (data?.status?.code === 200) {
          this.sessionId = data.result.session_id;
          this.sessionDatetime = data.result.datetime;
          console.log('✅ JotURL Authentication successful!');
          return true;
        } else {
          console.error('❌ JotURL Authentication failed:', data?.status?.text);
          console.error('   Error:', data?.status?.error);
          return false;
        }
      } else {
        console.error(`❌ JotURL Authentication failed (HTTP ${response.status})`);
        const text = await response.text();
        console.error('   Response:', text);
        return false;
      }
    } catch (error) {
      console.error('❌ JotURL login error:', error);
      return false;
    }
  }

  /**
   * Create a shortened deep link
   */
  async createShortLink(destinationUrl, alias = null, domainId = null) {
    try {
      // Ensure we have a valid session
      if (!this.sessionId) {
        const loginSuccess = await this.login();
        if (!loginSuccess) {
          throw new Error('Failed to authenticate with JotURL');
        }
      }

      const { authHash, gmtDatetime } = this._generateAuthHash();

      // Step 1: Create the shortened link
      const shortenUrl = new URL("https://joturl.com/a/i1/urls/shorten");
      shortenUrl.searchParams.append('_sid', this.sessionId);
      shortenUrl.searchParams.append('_h', authHash);
      shortenUrl.searchParams.append('_dt', gmtDatetime);
      shortenUrl.searchParams.append('project_id', this.projectId);
      shortenUrl.searchParams.append('long_url', destinationUrl);
      shortenUrl.searchParams.append('domain_id', domainId || this.domainId);

      const headers = {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json'
      };

      console.log('🔗 Creating short link...');

      const shortenResponse = await fetch(shortenUrl.toString(), {
        method: 'POST',
        headers
      });

      if (!shortenResponse.ok) {
        const text = await shortenResponse.text();

        // Check if session expired
        if (text.includes('INVALID session') || shortenResponse.status === 401) {
          console.log('Session expired, re-authenticating...');
          this.sessionId = null;
          const loginSuccess = await this.login();
          if (loginSuccess) {
            // Retry creating the link
            return await this.createShortLink(destinationUrl, alias, domainId);
          }
        }

        throw new Error(`API Error (Status ${shortenResponse.status}): ${text}`);
      }

      const shortenData = await shortenResponse.json();

      if (shortenData?.status?.code !== 200) {
        const errorMsg = shortenData?.status?.error || 'Unknown error';
        throw new Error(`Error creating link: ${errorMsg}`);
      }

      let shortUrl = shortenData.result.short_url;
      const linkId = shortenData.result.id;

      // Fix URL if it starts with //
      if (shortUrl.startsWith('//')) {
        shortUrl = 'https:' + shortUrl;
      }

      console.log('✅ Short link created:', shortUrl);

      // Step 2: Get the deep link configuration
      await this._enableDeepLink(linkId, headers);

      return shortUrl;
    } catch (error) {
      console.error('❌ Error creating short link:', error);
      throw error;
    }
  }

  /**
   * Enable deep link for a shortened URL
   */
  async _enableDeepLink(linkId, headers) {
    try {
      const { authHash, gmtDatetime } = this._generateAuthHash();

      // Step 2: Get deep link info
      const deeplinkInfoUrl = "https://joturl.com/a/i1/urls/easydeeplinks/info";
      const deeplinkPayload = {
        _sid: this.sessionId,
        _h: authHash,
        _dt: gmtDatetime,
        id: linkId
      };

      console.log('🔍 Fetching deep link info...');

      const deeplinkResponse = await fetch(deeplinkInfoUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(deeplinkPayload)
      });

      if (!deeplinkResponse.ok) {
        console.warn('⚠ Failed to fetch deep link info');
        return;
      }

      const deeplinkData = await deeplinkResponse.json();

      if (deeplinkData?.result?.autodetect === 1) {
        // Step 3: Apply the deep link configuration
        const editUrl = "https://joturl.com/a/i1/urls/easydeeplinks/edit";
        const settings = deeplinkData.result;

        const editPayload = {
          _sid: this.sessionId,
          _h: authHash,
          _dt: gmtDatetime,
          id: linkId,
          settings: JSON.stringify(settings)
        };

        console.log('⚙️ Applying deep link settings...');

        const editResponse = await fetch(editUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify(editPayload)
        });

        if (editResponse.ok) {
          const editResult = await editResponse.json();
          if (editResult?.status?.code === 200) {
            console.log(`✓ Deep link enabled for ${linkId}`);
          } else {
            const errorMsg = editResult?.status?.error || 'Unknown error';
            console.warn(`❌ Failed to enable deep link: ${errorMsg}`);
          }
        } else {
          console.warn(`❌ Failed to enable deep link - Status ${editResponse.status}`);
        }
      } else {
        console.warn('⚠ Deep link autodetect failed');
      }
    } catch (error) {
      console.warn('⚠ Error enabling deep link:', error);
      // Don't throw - deep link is optional
    }
  }
}

// Singleton instance
let jotURLInstance = null;

export const initializeJotURL = (email, publicKey, privateKey) => {
  jotURLInstance = new JotURLService(email, publicKey, privateKey);
  return jotURLInstance;
};

export const getJotURLInstance = () => {
  if (!jotURLInstance) {
    throw new Error('JotURL service not initialized. Call initializeJotURL first.');
  }
  return jotURLInstance;
};

export default JotURLService;